import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../api";

// Estructura persistida en localStorage bajo clave SEND_JOB_V1
// {
//   status: 'idle'|'scheduled'|'sending'|'stopped'|'done',
//   scheduledAt: number | null, // epoch ms
//   total: number,
//   current: number,
//   sent: number,
//   failed: number,
//   index: number, // próximo índice a enviar
//   intervalMs: number,
//   campaign: string | null,
//   recipients: Array<{ id?: any, phone: string }>,
//   templates: Array<{ id: any, title: string, content: string }>,
// }

const STORAGE_KEY = "SEND_JOB_V1";

const SendManagerContext = createContext(null);

export const useSendManager = () => useContext(SendManagerContext);

const htmlToPlain = (html) => {
  try {
    let s = String(html || "");
    s = s.replace(/<\s*br\s*\/?>(?=\n|\r|$)/gi, "\n");
    s = s.replace(/<\s*br\s*\/?>(?!\n|\r)/gi, "\n");
    s = s.replace(/<\/(p|div|h[1-6]|li)\s*>/gi, "\n");
    s = s.replace(/<li[^>]*>/gi, "• ");
    s = s.replace(/<[^>]+>/g, "");
    // Decodificar entidades básicas
    s = s.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
    return s;
  } catch {
    return String(html || "");
  }
};

// Elimina únicamente espacios y saltos de línea al final (sin tocar interior)
const rtrimLines = (text) => {
  try {
    return String(text || '').replace(/[ \t\f\v]+$/gm, '') // quitar espacios finales por línea
      .replace(/(?:\r?\n)+$/,''); // quitar saltos finales acumulados
  } catch { return text; }
};

const extractFirstImageSrc = (html) => {
  try {
    const m = String(html || "").match(/<img[^>]+src=["']([^"']+)["']/i);
    return m ? m[1] : null;
  } catch {
    return null;
  }
};

const extractFirstVideoUrl = (html) => {
  try {
    const container = document.createElement('div');
    container.innerHTML = String(html || "");
    // 1) <video src>
    const vids = container.getElementsByTagName('video');
    for (const v of vids) {
      const src = v.getAttribute('src') || (v.querySelector('source')?.getAttribute('src'));
      if (src) return src;
    }
    // 2) <a href> que apunte a extensiones de video o con texto indicativo
    const anchors = container.getElementsByTagName('a');
    for (const a of anchors) {
      const href = a.getAttribute('href') || '';
      const text = (a.textContent || '').toLowerCase();
      if (!href) continue;
      if (/\.(mp4|mov|webm|m4v)(\?|#|$)/i.test(href) || text.includes('video') || text.includes('▶')) {
        return href;
      }
    }
    return null;
  } catch {
    return null;
  }
};

// El editor inserta un enlace con texto "▶ Video adjunto". Cuando hay video,
// limpiamos ese placeholder del texto plano para evitar que se envíe como caption.
const stripVideoPlaceholder = (plainText) => {
  try {
    let s = String(plainText || "");
    // Remover variantes del placeholder con o sin el símbolo ▶ y espacios
    // - Coincide: "▶ Video adjunto", "video adjunto", con mayúsculas/minúsculas
    s = s.replace(/(^|\s)[\u25B6>]*\s*video\s+adjunto(\s|$)/gi, ' ');
    // Colapsar espacios y saltos de línea redundantes
    s = s.replace(/[ \t\f\v]+/g, ' ');
    s = s.replace(/\n{3,}/g, '\n\n');
    s = s.trim();
    return s;
  } catch {
    return String(plainText || "");
  }
};

export function SendManagerProvider({ children }) {
  const scheduleTimerRef = useRef(null);
  const stepTimerRef = useRef(null);
  const retryTimerRef = useRef(null); // reintentos cuando está programado pero no listo
  // Polling batch servidor
  const batchPollRef = useRef(null);
  const BATCH_STORAGE_KEY = 'SERVER_BATCH_ID_V1';
  const [batch, setBatch] = useState(() => {
    try {
      const id = localStorage.getItem(BATCH_STORAGE_KEY);
      return id ? { batchId: id, status: 'unknown' } : null;
    } catch { return null; }
  });
  const [job, setJob] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { status: 'idle' };
      const parsed = JSON.parse(raw);
      return parsed || { status: 'idle' };
    } catch {
      return { status: 'idle' };
    }
  });

  // Persistir cambios relevantes
  useEffect(() => {
    try {
      if (!job || job.status === 'idle') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(job));
      }
    } catch {}
  }, [job]);

  // Envío de un mensaje (replicado desde Mensajes.jsx)
  const sendOne = useCallback(async ({ toPhone, template, campaign, runId }) => {
    const imgUrl = extractFirstImageSrc(template?.content);
    const vidUrl = extractFirstVideoUrl(template?.content);
    // Texto plano base
  let baseText = htmlToPlain(template?.content);
  baseText = rtrimLines(baseText);
    // Si hay video, remover el placeholder "Video adjunto"; si queda vacío, no enviar caption
  let contentText = vidUrl ? stripVideoPlaceholder(baseText) : baseText;
  contentText = rtrimLines(contentText);
    const payload = {
      to: toPhone,
      template_id: template?.id,
      title: template?.title,
      content: contentText,
      campaign: campaign || null,
      run_id: runId || null,
      // Priorizar el video; si no hay, usar imagen
      media_url: vidUrl || undefined,
      image_url: vidUrl ? undefined : (imgUrl || undefined),
    };
    try {
      try {
        // Enforce a client-side timeout to evitar cuelgues si el servicio tarda demasiado (p.ej. media pesada)
        const r = await api.post("messages/send/", payload, { timeout: 45000 });
        if (!(r && (r.status >= 200 && r.status < 300))) throw new Error(`HTTP ${r?.status}`);
  } catch (proxyErr) {
        // Si el backend rechazó por límite (400), no hacer fallback
        const status = proxyErr?.response?.status;
        const detail = proxyErr?.response?.data?.detail || proxyErr?.message || '';
        if (status === 400) {
          return { ok: false, error: detail || "Solicitud inválida" };
        }
        // Fallback directo al servicio WhatsApp
        const fallbackUrl = import.meta.env.VITE_SEND_URL || 'http://127.0.0.1:3001/send';
        const clientId = localStorage.getItem('username') || localStorage.getItem('userId') || '';
        // Mapear campos al contrato del servicio Node
        const body = {
          number: toPhone,
          message: contentText,
          clientId,
        };
        if (vidUrl) body.mediaUrl = vidUrl; else if (imgUrl) body.imageUrl = imgUrl;
        // Implementar timeout en fetch fallback
        const ctrl = new AbortController();
        const t = setTimeout(() => { try { ctrl.abort(); } catch(_) {} }, 25000);
        let resp;
        try {
          resp = await fetch(fallbackUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            signal: ctrl.signal,
          });
        } finally {
          clearTimeout(t);
        }
        if (!resp.ok) {
          let msg = `HTTP ${resp.status}`;
          try {
            const j = await resp.json();
            if (j && (j.error || j.detail)) msg = String(j.error || j.detail);
          } catch {}
          // Mapear errores comunes
          if (/clientId requerido/i.test(msg)) {
            msg = 'Sesión no iniciada: vincula WhatsApp en “Mensajes en línea”.';
          } else if (/not.*ready|ready.*false/i.test(msg)) {
            msg = 'El cliente de WhatsApp no está listo aún. Intenta de nuevo cuando esté en estado "ready".';
          }
          return { ok: false, error: msg };
        }
        // Fallback exitoso: registrar manualmente en backend para que cuente en métricas
        try {
          const logPayload = {
            to: toPhone,
            content: contentText,
            campaign: campaign || null,
            run_id: runId || null,
          };
          if (campaign) {
            await api.post('messages/log/', logPayload);
          }
        } catch (_) { /* silencioso */ }
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "error" };
    }
  }, []);

  const clearTimers = () => {
    if (scheduleTimerRef.current) { clearTimeout(scheduleTimerRef.current); scheduleTimerRef.current = null; }
    if (stepTimerRef.current) { clearTimeout(stepTimerRef.current); stepTimerRef.current = null; }
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
  };

  const stopJob = useCallback(() => {
    clearTimers();
    setJob((prev) => ({ ...prev, status: 'stopped' }));
  }, []);

  const completeJob = useCallback(() => {
    clearTimers();
    setJob((prev) => ({ ...prev, status: 'done' }));
  }, []);

  const checkReady = useCallback(async () => {
    try {
      const { data } = await api.get('whatsapp/status/');
      if (data && data.chatFeatureDisabled) {
        try { localStorage.setItem('chat_feature_disabled', '1'); } catch {}
      }
      return !!data?.ready;
    } catch {
      return false;
    }
  }, []);

  const startJob = useCallback(async (initial) => {
    // initial: { recipients, templates, campaign, resume?, runId?, startIndex? }
    // Nueva lógica: intervalo aleatorio entre 7 y 20 segundos (configurable aquí)
    const MIN_DELAY_SEC = 7;
    const MAX_DELAY_SEC = 20;
    const randDelayMs = () => (Math.floor(Math.random() * (MAX_DELAY_SEC - MIN_DELAY_SEC + 1)) + MIN_DELAY_SEC) * 1000;

    const recipients = initial.recipients || job.recipients || [];
    const templates = initial.templates || job.templates || [];
    const campaign = initial.campaign ?? job.campaign ?? null;
    const startIndex = initial.startIndex ?? job.index ?? 0;

    const total = recipients.length;
    // Verificar estado del puente WhatsApp antes de iniciar
    const ready = await checkReady();
    if (!ready) {
      throw new Error('El cliente de WhatsApp no está listo. Ve a la página “Mensajes en línea” y vincula tu sesión.');
    }

    // Generar un run_id para esta corrida.
    // Regla: por defecto SIEMPRE crear un run_id nuevo en cada startJob (nueva corrida).
    // Solo reutilizar el run_id previo cuando se indique explícitamente que estamos reanudando (resume=true).
    const genRunId = () => ((typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const resume = !!initial?.resume;
    const runId = resume
      ? (initial?.runId || job?.run_id || genRunId())
      : (initial?.runId || genRunId());
    // Si es reanudación y tenemos un nextSendAt válido en el pasado, enviar inmediatamente; si está en el futuro mantenerlo.
    // Primer mensaje: enviar inmediatamente salvo que estemos reanudando y haya un nextSendAt futuro.
    let initialDelayMs = 0;
    let preservedNextSendAt = null;
    if (initial.resume && job?.nextSendAt) {
      if (job.nextSendAt > Date.now()) {
        preservedNextSendAt = job.nextSendAt; // esperar hasta este tiempo para primer mensaje pendiente
        initialDelayMs = job.nextSendAt - Date.now();
      }
    }
    setJob({
      status: 'sending',
      scheduledAt: null,
      total,
      current: startIndex, // aún no enviado
      sent: 0,
      failed: 0,
      index: startIndex,
      // Guardamos rango fijo por si luego queremos mostrarlo en UI o hacer ajustes
      minDelaySec: MIN_DELAY_SEC,
      maxDelaySec: MAX_DELAY_SEC,
  // Para el primer envío (inmediato) no definimos próximo; se calculará tras el primer mensaje.
  nextDelayMs: preservedNextSendAt ? initialDelayMs : null,
  nextSendAt: preservedNextSendAt ? preservedNextSendAt : null,
      campaign,
      recipients,
      templates,
      run_id: runId,
    });

    const step = async (i, acc) => {
      // acc: { sent, failed }
      if (i >= total) { completeJob(); return; }
      // Guardar progreso visible
      setJob((prev) => ({ ...prev, current: i + 1, index: i + 1, sent: acc.sent, failed: acc.failed }));
      const rec = recipients[i];
      const tpl = templates[i % Math.max(templates.length, 1)];
  const result = await sendOne({ toPhone: rec.phone, template: tpl, campaign, runId });
      const nextAcc = {
        sent: acc.sent + (result.ok ? 1 : 0),
        failed: acc.failed + (result.ok ? 0 : 1),
      };
      setJob((prev) => ({ ...prev, sent: nextAcc.sent, failed: nextAcc.failed }));
      // Programar siguiente paso con retraso aleatorio si quedan más
      if (i + 1 < total) {
        const delayMs = randDelayMs();
        const plannedAt = Date.now() + delayMs;
        setJob((prev) => ({ ...prev, nextDelayMs: delayMs, nextSendAt: plannedAt }));
        stepTimerRef.current = setTimeout(() => step(i + 1, nextAcc), delayMs);
      } else {
        // Último mensaje enviado, limpiar indicadores de próximo envío
        setJob((prev) => ({ ...prev, nextDelayMs: null, nextSendAt: null }));
        completeJob();
      }
    };
    // Programar primer envío (si initialDelayMs > 0) o ejecutar inmediato si 0
    const launch = () => step(startIndex, { sent: 0, failed: 0 });
    if (initialDelayMs > 0) {
      // Reanudación con un envío futuro programado
      stepTimerRef.current = setTimeout(launch, initialDelayMs);
    } else {
      // Envío inmediato del primero
      launch();
    }
  }, [checkReady, completeJob, job.campaign, job.index, job.intervalMs, job.recipients, job.templates, sendOne]);

  const scheduleJob = useCallback(({ at, recipients, templates, campaign, intervalMs = 10000 }) => {
    const when = at instanceof Date ? at.getTime() : Number(at);
    const now = Date.now();
    if (!when || isNaN(when) || when <= now) {
      throw new Error("La fecha y hora programadas deben ser futuras.");
    }
    clearTimers();
    const total = recipients.length;
    setJob({
      status: 'scheduled',
      scheduledAt: when,
      total,
      current: 0,
      sent: 0,
      failed: 0,
      index: 0,
      intervalMs,
      campaign: campaign || null,
      recipients,
      templates,
    });
    const attempt = async () => {
      try {
        await startJob({ recipients, templates, campaign, intervalMs, startIndex: 0 });
      } catch (e) {
        // Si no está listo aún, reintentar en 10s sin perder estado programado
        retryTimerRef.current = setTimeout(attempt, 10000);
      }
    };
    scheduleTimerRef.current = setTimeout(() => { attempt(); }, Math.max(0, when - now));
  }, [startJob]);

  // Restaurar programaciones o envíos activos al montar
  useEffect(() => {
    if (!job || job.status === 'idle' || job.status === 'done' || job.status === 'stopped') return;
    // Si estaba programado, reprogramar; si ya pasó la hora, iniciar inmediatamente
    if (job.status === 'scheduled') {
      const now = Date.now();
      const when = Number(job.scheduledAt || 0);
      if (when && when > now) {
        scheduleTimerRef.current = setTimeout(() => {
          // Reusar recipients/templates guardados en job
          const attempt = async () => {
            try {
              await startJob({ startIndex: job.index || 0 });
            } catch {
              retryTimerRef.current = setTimeout(attempt, 10000);
            }
          };
          attempt();
        }, when - now);
      } else {
        const attempt = async () => {
          try {
            await startJob({ startIndex: job.index || 0 });
          } catch {
            retryTimerRef.current = setTimeout(attempt, 10000);
          }
        };
        attempt();
      }
    }
    if (job.status === 'sending') {
      // Reanudar desde índice almacenado conservando el run_id de la corrida activa
      startJob({ startIndex: job.index || 0, resume: true, runId: job.run_id });
    }
    // Limpieza al desmontar
    return () => { clearTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    job,
    batch,
    scheduleJob,
    startJob,
    stopJob,
    // === Server batch API ===
    startServerBatch: async ({ recipients, templates, campaign }) => {
      if (!Array.isArray(recipients) || recipients.length === 0) throw new Error('Sin destinatarios');
      if (!Array.isArray(templates) || templates.length === 0) throw new Error('Sin plantillas');
      const WA_BASE = import.meta.env.VITE_WA_BASE || '/wa';
      // Preparar items ciclando plantillas
      const items = recipients.map((r, idx) => {
        const tpl = templates[idx % templates.length];
        const rawHtml = tpl?.content || '';
        // Detectar media (priorizar video sobre imagen como en sendOne)
        const vidUrl = extractFirstVideoUrl(rawHtml);
        const imgUrl = vidUrl ? null : extractFirstImageSrc(rawHtml);
        // Texto plano para caption
        let plain = htmlToPlain(rawHtml || '').slice(0, 4096);
        if (vidUrl) {
          // Quitar placeholder de video para no enviarlo como caption
            plain = stripVideoPlaceholder(plain).slice(0, 4096);
        }
        // Si tras limpiar queda vacío, dejar caption vacío (WhatsApp permite caption vacío)
        plain = rtrimLines(plain);
        if (!plain || !plain.trim()) plain = '';
        const item = { number: r.phone, message: plain };
        if (vidUrl) {
          item.mediaUrl = vidUrl; // el backend Node lo recibirá y lo normaliza
        } else if (imgUrl) {
          // Acepta imageUrl o mediaUrl; mantenemos imageUrl para claridad y el backend lo colapsa a mediaUrl
          item.imageUrl = imgUrl;
        }
        return item;
      });
      const clientId = localStorage.getItem('username') || localStorage.getItem('userId') || '';
      const body = { clientId, items, minDelaySec: 7, maxDelaySec: 20 };
      let resp;
      try {
        // Verificar que la sesión exista y esté lista primero para dar mensaje claro
        if (clientId) {
          try {
            const stR = await fetch(`${WA_BASE}/status?clientId=${encodeURIComponent(clientId)}`);
            if (stR.ok) {
              const stJ = await stR.json().catch(()=>null);
              if (stJ && !stJ.ready) {
                throw new Error('Tu sesión de WhatsApp no está lista. Ve a la página de mensajes en línea y escanea el QR.');
              }
            }
          } catch (preErr) {
            if (preErr instanceof Error) throw preErr;
          }
        } else {
          throw new Error('No se encontró clientId (username). Inicia sesión de usuario primero.');
        }
        resp = await fetch(`${WA_BASE}/batch/send`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
        });
      } catch (e) { throw new Error('No se pudo contactar al servicio de WhatsApp'); }
      if (!resp.ok) {
        let detail = '';
        const ct = resp.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          try { const j = await resp.json(); detail = j.detail || j.error || JSON.stringify(j); } catch {}
        } else {
          try { detail = (await resp.text()).slice(0,400); } catch {}
        }
        if (resp.status === 404) throw new Error('Ruta /batch/send no encontrada en el servicio (¿reiniciaste el servicio WhatsApp tras agregar batch?).');
        if (resp.status === 409) throw new Error(detail || 'Sesión no lista (escanea el QR primero).');
        if (resp.status === 400) throw new Error(detail || 'Petición inválida al iniciar batch.');
        if (detail.startsWith('<!DOCTYPE')) throw new Error('El proxy devolvió HTML (probablemente el servicio WhatsApp no está corriendo en :3001 o el proxy /wa no aplica).');
        throw new Error(detail || `Error iniciando batch (HTTP ${resp.status})`);
      }
      const data = await resp.json();
      if (!data.batchId) throw new Error('Respuesta inválida del servicio');
      try { localStorage.setItem(BATCH_STORAGE_KEY, data.batchId); } catch {}
      setBatch({ batchId: data.batchId, status: 'running' });
      // iniciar polling
      if (batchPollRef.current) clearInterval(batchPollRef.current);
      const poll = async () => {
        try {
          const r = await fetch(`${WA_BASE}/batch/status?batchId=${encodeURIComponent(data.batchId)}`);
            if (!r.ok) {
              if (r.status === 404) {
                setBatch(null);
                try { localStorage.removeItem(BATCH_STORAGE_KEY); } catch {}
              }
              return;
            }
            const st = await r.json();
            setBatch(prev => ({ ...(prev||{}), ...st }));
            if (st.status === 'done' || st.status === 'cancelled') {
              clearInterval(batchPollRef.current); batchPollRef.current = null;
              try { localStorage.removeItem(BATCH_STORAGE_KEY); } catch {}
            }
        } catch {}
      };
      poll();
      batchPollRef.current = setInterval(poll, 4000);
    },
    cancelServerBatch: async () => {
      if (!batch || !batch.batchId) return;
      const WA_BASE = import.meta.env.VITE_WA_BASE || '/wa';
      try {
        await fetch(`${WA_BASE}/batch/cancel`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ batchId: batch.batchId }) });
      } catch {}
      if (batchPollRef.current) { clearInterval(batchPollRef.current); batchPollRef.current = null; }
      setBatch(prev => prev ? { ...prev, status: 'cancelled', nextPlannedAt: null } : prev);
      try { localStorage.removeItem(BATCH_STORAGE_KEY); } catch {}
    },
    clear: () => setJob({ status: 'idle' }),
  };

  // Reanudación de batch servidor si había uno guardado
  useEffect(() => {
    if (!batch || !batch.batchId || batch.status !== 'unknown') return;
    const WA_BASE = import.meta.env.VITE_WA_BASE || '/wa';
    const poll = async () => {
      try {
        const r = await fetch(`${WA_BASE}/batch/status?batchId=${encodeURIComponent(batch.batchId)}`);
        if (!r.ok) {
          if (r.status === 404) {
            setBatch(null); try { localStorage.removeItem(BATCH_STORAGE_KEY); } catch {}
          }
          return;
        }
        const st = await r.json();
        setBatch(prev => ({ ...(prev||{}), ...st }));
        if (st.status === 'done' || st.status === 'cancelled') {
          if (batchPollRef.current) { clearInterval(batchPollRef.current); batchPollRef.current = null; }
          try { localStorage.removeItem(BATCH_STORAGE_KEY); } catch {}
        }
      } catch {}
    };
    poll();
    if (batchPollRef.current) clearInterval(batchPollRef.current);
    batchPollRef.current = setInterval(poll, 4000);
    return () => { if (batchPollRef.current) { clearInterval(batchPollRef.current); batchPollRef.current = null; } };
  }, [batch?.batchId]);

  return (
    <SendManagerContext.Provider value={value}>
      {children}
    </SendManagerContext.Provider>
  );
}

// Reanudar batch almacenado al montar provider
// (fuera del componente exportado para mantener orden; se podría integrar dentro con un useEffect extra)
